import React, { useEffect, useState, useRef, useCallback } from 'react';
import './AdDisplay.css';

// ============================================
// PERFORMANCE OPTIMIZATIONS FOR VIEWABILITY
// ============================================

const AD_DOMAINS = [
  'https://securepubads.g.doubleclick.net',
  'https://pagead2.googlesyndication.com',
  'https://www.googletagservices.com',
  'https://tpc.googlesyndication.com',
  'https://googleads.g.doubleclick.net',
];

let preconnectInjected = false;
function injectPreconnectHints() {
  if (preconnectInjected || typeof document === 'undefined') return;
  preconnectInjected = true;
  
  const fragment = document.createDocumentFragment();
  AD_DOMAINS.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    fragment.appendChild(link);
    
    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = domain;
    fragment.appendChild(dnsPrefetch);
  });
  document.head.appendChild(fragment);
}

if (typeof window !== 'undefined') {
  injectPreconnectHints();
}

// ============================================
// INTERSECTION OBSERVER FOR LAZY LOADING
// ============================================
const observerCallbacks = new Map();
let globalObserver = null;

function getIntersectionObserver() {
  if (globalObserver) return globalObserver;
  
  if (typeof IntersectionObserver === 'undefined') return null;
  
  globalObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        const callback = observerCallbacks.get(entry.target);
        if (callback) {
          callback(entry.isIntersecting, entry);
        }
      });
    },
    {
      rootMargin: '300px 0px',
      threshold: [0, 0.1, 0.5, 1.0]
    }
  );
  
  return globalObserver;
}

function observeElement(element, callback) {
  const observer = getIntersectionObserver();
  if (!observer || !element) return () => {};
  
  observerCallbacks.set(element, callback);
  observer.observe(element);
  
  return () => {
    observerCallbacks.delete(element);
    observer.unobserve(element);
  };
}

// ============================================
// GLOBAL AD REGISTRY
// ============================================
const adRegistry = {
  rendered: new Set(),
  slots: {},
  interstitial: false,
  initialized: new Set(),
};

function isAdRendered(key) {
  return adRegistry.rendered.has(key);
}

function markAdRendered(key) {
  adRegistry.rendered.add(key);
}

function getAdKey(ad, position) {
  const code = getCode(ad);
  const codeHash = code ? hashCode(code) : '';
  const pathHash = ad.gamConfig?.adUnitPath || '';
  return `${ad._id || 'custom'}-${position}-${ad.adType}-${pathHash}-${codeHash}`;
}

function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
}

function getCode(ad) {
  return ad.code || ad.customCode || ad.htmlCode || ad.jsCode || '';
}

// ============================================
// GOOGLE PUBLISHER TAG (GPT.JS) LOADER - FIXED
// ============================================
let gptLoaded = false;
let gptReady = false;
let gptServicesEnabled = false;
let gptReadyCallbacks = [];
let pendingSlots = [];
let refreshTimeout = null;

function loadGPTScript() {
  return new Promise((resolve) => {
    // Already fully ready
    if (gptReady && gptServicesEnabled) {
      resolve();
      return;
    }

    // Script loading, add to callback queue
    if (gptLoaded && !gptReady) {
      gptReadyCallbacks.push(resolve);
      return;
    }

    // Check if script exists but not initialized
    if (document.querySelector('script[src*="securepubads.g.doubleclick.net"]')) {
      gptLoaded = true;
      if (window.googletag && window.googletag.apiReady) {
        // API ready but services may not be enabled
        window.googletag.cmd.push(function() {
          if (!gptServicesEnabled) {
            window.googletag.pubads().enableSingleRequest();
            window.googletag.pubads().enableLazyLoad({
              fetchMarginPercent: 500,
              renderMarginPercent: 200,
              mobileScaling: 2.0
            });
            window.googletag.enableServices();
            gptServicesEnabled = true;
            console.log('[GPT] Services enabled (existing script)');
          }
          gptReady = true;
          resolve();
        });
        return;
      }
      gptReadyCallbacks.push(resolve);
      return;
    }

    // Load script fresh
    gptLoaded = true;
    window.googletag = window.googletag || { cmd: [] };

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
    
    script.onload = () => {
      window.googletag.cmd.push(function() {
        if (!gptServicesEnabled) {
          window.googletag.pubads().enableSingleRequest();
          window.googletag.pubads().enableLazyLoad({
            fetchMarginPercent: 500,
            renderMarginPercent: 200,
            mobileScaling: 2.0
          });
          window.googletag.enableServices();
          gptServicesEnabled = true;
          gptReady = true;
          
          console.log('[GPT] Services enabled (new script)');
          
          resolve();
          gptReadyCallbacks.forEach(cb => cb());
          gptReadyCallbacks = [];
        }
      });
    };

    script.onerror = () => {
      console.error('[GPT] Failed to load script');
      resolve();
    };
    document.head.appendChild(script);
  });
}

function whenGPTReady(callback) {
  window.googletag = window.googletag || { cmd: [] };
  
  if (gptReady && gptServicesEnabled) {
    window.googletag.cmd.push(callback);
  } else {
    loadGPTScript().then(() => {
      window.googletag.cmd.push(callback);
    });
  }
}


function queueSlotRefresh(slot) {
  if (!slot) return;
  
  pendingSlots.push(slot);
  
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
  }
  
  refreshTimeout = setTimeout(() => {
    if (pendingSlots.length === 0) return;
    
    const slotsToRefresh = [...pendingSlots];
    pendingSlots = [];
    
    window.googletag = window.googletag || { cmd: [] };
    window.googletag.cmd.push(function() {
      // Double check services are enabled
      if (!gptServicesEnabled) {
        console.log('[GAM] Enabling services before refresh...');
        try {
          window.googletag.pubads().enableSingleRequest();
          window.googletag.enableServices();
          gptServicesEnabled = true;
        } catch (e) {
          console.error('[GAM] Error enabling services:', e);
        }
      }
      
      try {
        console.log('[GAM] Refreshing', slotsToRefresh.length, 'slots');
        window.googletag.pubads().refresh(slotsToRefresh);
      } catch (e) {
        console.error('[GAM] Refresh error:', e);
      }
    });
  }, 200);
}

function isOutOfPageAd(adUnitPath) {
  if (!adUnitPath) return false;
  const path = adUnitPath.toLowerCase();
  return path.includes('inter') || 
         path.includes('interstitial') || 
         path.includes('anchor') ||
         path.includes('rewarded') ||
         path.includes('oop');
}

// ============================================
// HEAD CODE ADS COMPONENT
// ============================================
export const HeadCodeAds = () => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;

    const initHeadAds = async () => {
      try {
        const headResponse = await fetch('/api/ads/position/head_code');
        const headData = await headResponse.json();

        if (headData.success && headData.data?.length > 0) {
          headData.data.forEach(ad => {
            const adKey = getAdKey(ad, 'head_code');
            
            if (isAdRendered(adKey)) return;
            
            const code = getCode(ad);
            if (!code || !ad.isActive) return;

            try {
              const temp = document.createElement('div');
              temp.innerHTML = code;
              
              Array.from(temp.childNodes).forEach(node => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  const clone = node.cloneNode(true);
                  clone.setAttribute('data-ad-key', adKey);
                  document.head.appendChild(clone);
                  
                  if (clone.tagName === 'SCRIPT') {
                    const newScript = document.createElement('script');
                    Array.from(clone.attributes).forEach(attr => {
                      newScript.setAttribute(attr.name, attr.value);
                    });
                    newScript.textContent = clone.textContent;
                    document.head.removeChild(clone);
                    document.head.appendChild(newScript);
                  }
                }
              });
              
              markAdRendered(adKey);
            } catch (e) {
              console.error('Error injecting head code:', e);
            }
          });
        }

        const gamCheckResponse = await fetch('/api/ads/check-gam');
        const gamData = await gamCheckResponse.json();

        if (gamData.success && gamData.hasGAM) {
          await loadGPTScript();
          await setupInterstitialFromDB();
        }

        setInitialized(true);
      } catch (error) {
        console.error('Error initializing head ads:', error);
      }
    };

    initHeadAds();
  }, [initialized]);

  return null;
};

async function setupInterstitialFromDB() {
  if (adRegistry.interstitial) return;
  
  try {
    const response = await fetch('/api/ads/position/interstitial');
    const data = await response.json();
    
    if (data.success && data.data?.length > 0) {
      const interstitialAd = data.data.find(ad => 
        ad.adType === 'gam' && ad.gamConfig?.adUnitPath
      );
      
      if (interstitialAd) {
        whenGPTReady(function() {
          if (adRegistry.interstitial) return;
          
          const slot = window.googletag.defineOutOfPageSlot(
            interstitialAd.gamConfig.adUnitPath,
            window.googletag.enums.OutOfPageFormat.INTERSTITIAL
          );
          
          if (slot) {
            slot.addService(window.googletag.pubads());
            adRegistry.interstitial = true;
          }
        });
      }
    }
  } catch (e) {}
}

// ============================================
// GAM AD COMPONENT - FIXED
// ============================================
const GAMAdUnit = ({ ad, divId, position, onImpression, priority }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [shouldInit, setShouldInit] = useState(priority === 'high');
  const containerRef = useRef(null);
  const initRef = useRef(false);

  // Lazy initialization based on visibility
  useEffect(() => {
    if (priority === 'high') {
      setShouldInit(true);
      return;
    }

    const element = containerRef.current;
    if (!element) return;

    const cleanup = observeElement(element, (isIntersecting) => {
      if (isIntersecting && !shouldInit) {
        setShouldInit(true);
      }
    });

    return cleanup;
  }, [priority, shouldInit]);

  useEffect(() => {
    if (!shouldInit || initRef.current) return;
    if (!ad.gamConfig?.adUnitPath) return;

    const adUnitPath = ad.gamConfig.adUnitPath;
    const slotKey = `gam-${position}-${adUnitPath}`;
    const isInterstitial = isOutOfPageAd(adUnitPath);

    if (adRegistry.slots[slotKey]) {
      initRef.current = true;
      setIsLoaded(true);
      return;
    }

    if (isInterstitial && adRegistry.interstitial) {
      initRef.current = true;
      return;
    }

    const initSlot = () => {
      const divElement = document.getElementById(divId);
      
      if (!isInterstitial && !divElement) {
        setTimeout(initSlot, 100);
        return;
      }

      initRef.current = true;

      // ⭐ FIXED: Load GPT và đợi services enabled trước
      loadGPTScript().then(() => {
        window.googletag.cmd.push(function() {
          // Double check slot chưa tồn tại
          if (adRegistry.slots[slotKey]) {
            setIsLoaded(true);
            return;
          }

          if (isInterstitial && adRegistry.interstitial) {
            return;
          }

          let slot;

          if (isInterstitial) {
            let format = window.googletag.enums.OutOfPageFormat.INTERSTITIAL;
            
            if (adUnitPath.toLowerCase().includes('anchor')) {
              format = adUnitPath.toLowerCase().includes('top') 
                ? window.googletag.enums.OutOfPageFormat.TOP_ANCHOR
                : window.googletag.enums.OutOfPageFormat.BOTTOM_ANCHOR;
            }

            slot = window.googletag.defineOutOfPageSlot(adUnitPath, format);
            if (slot) {
              adRegistry.interstitial = true;
            }
          } else {
            let adSize;
            try {
              adSize = JSON.parse(ad.gamConfig.size);
            } catch {
              adSize = [[300, 250]];
            }

            slot = window.googletag.defineSlot(adUnitPath, adSize, divId);
          }

          if (!slot) {
            console.warn('[GAM] Failed to define slot:', adUnitPath);
            return;
          }

          slot.addService(window.googletag.pubads());

          if (!isInterstitial && ad.gamConfig.sizeMapping) {
            try {
              const sizeMappingConfig = JSON.parse(ad.gamConfig.sizeMapping);
              const mapping = window.googletag.sizeMapping();
              sizeMappingConfig.forEach(([viewport, sizes]) => {
                mapping.addSize(viewport, sizes);
              });
              slot.defineSizeMapping(mapping.build());
            } catch (e) {}
          }

          if (ad.gamConfig.targeting) {
            try {
              const targetingObj = JSON.parse(ad.gamConfig.targeting);
              Object.entries(targetingObj).forEach(([key, value]) => {
                slot.setTargeting(key, value);
              });
            } catch (e) {}
          }

          adRegistry.slots[slotKey] = slot;

          if (!isInterstitial) {
            // ⭐ FIXED: Display trước, refresh sau
            window.googletag.display(divId);
            
            // Queue refresh (function đã handle việc đợi services)
            queueSlotRefresh(slot);
          }

          setIsLoaded(true);
          if (onImpression) onImpression();
        });
      });
    };

    // Start initialization
    if (priority === 'high') {
      initSlot();
    } else {
      setTimeout(initSlot, 100);
    }
  }, [shouldInit, ad.gamConfig?.adUnitPath, divId, position, onImpression, priority, ad.gamConfig?.size, ad.gamConfig?.sizeMapping, ad.gamConfig?.targeting]);

  if (isOutOfPageAd(ad.gamConfig?.adUnitPath)) {
    return null;
  }

  let containerSize = { minWidth: '300px', minHeight: '250px' };
  try {
    const sizes = JSON.parse(ad.gamConfig?.size || '[[300,250]]');
    if (Array.isArray(sizes) && sizes.length > 0) {
      const firstSize = Array.isArray(sizes[0]) ? sizes[0] : sizes;
      containerSize = {
        minWidth: `${firstSize[0]}px`,
        minHeight: `${firstSize[1]}px`
      };
    }
  } catch {}

  return (
    <div 
      ref={containerRef}
      id={divId} 
      className={`gam-ad-container ${isLoaded ? 'loaded' : 'loading'}`}
      style={containerSize}
    />
  );
};

// ============================================
// ADSENSE AD COMPONENT
// ============================================
const AdSenseUnit = ({ ad, adKey, onImpression, priority }) => {
  const containerRef = useRef(null);
  const initRef = useRef(false);
  const [shouldInit, setShouldInit] = useState(priority === 'high');

  useEffect(() => {
    if (priority === 'high') {
      setShouldInit(true);
      return;
    }

    const element = containerRef.current;
    if (!element) return;

    const cleanup = observeElement(element, (isIntersecting) => {
      if (isIntersecting && !shouldInit) {
        setShouldInit(true);
      }
    });

    return cleanup;
  }, [priority, shouldInit]);

  useEffect(() => {
    if (!shouldInit || initRef.current) return;
    if (!ad.adsenseConfig?.adClient || !ad.adsenseConfig?.adSlot) return;

    initRef.current = true;

    if (isAdRendered(adKey)) return;

    const loadAndPush = () => {
      if (!document.querySelector('script[src*="pagead2.googlesyndication.com"]')) {
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
        script.setAttribute('data-ad-client', ad.adsenseConfig.adClient);
        script.crossOrigin = 'anonymous';
        document.head.appendChild(script);
      }

      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        markAdRendered(adKey);
        if (onImpression) onImpression();
      } catch (e) {}
    };

    if (priority === 'high') {
      loadAndPush();
    } else {
      setTimeout(loadAndPush, 100);
    }
  }, [shouldInit, ad, adKey, onImpression, priority]);

  return (
    <ins
      ref={containerRef}
      className="adsbygoogle"
      style={{ display: 'block' }}
      data-ad-client={ad.adsenseConfig?.adClient}
      data-ad-slot={ad.adsenseConfig?.adSlot}
      data-ad-format={ad.adsenseConfig?.adFormat || 'auto'}
      data-full-width-responsive={ad.adsenseConfig?.fullWidthResponsive !== false ? 'true' : 'false'}
    />
  );
};

// ============================================
// CUSTOM CODE AD
// ============================================
const CustomCodeAd = ({ ad, adKey, onImpression, priority }) => {
  const containerRef = useRef(null);
  const injectedRef = useRef(false);
  const [shouldInit, setShouldInit] = useState(priority === 'high');

  useEffect(() => {
    if (priority === 'high') {
      setShouldInit(true);
      return;
    }

    const element = containerRef.current;
    if (!element) return;

    const cleanup = observeElement(element, (isIntersecting) => {
      if (isIntersecting && !shouldInit) {
        setShouldInit(true);
      }
    });

    return cleanup;
  }, [priority, shouldInit]);

  useEffect(() => {
    if (!shouldInit || injectedRef.current) return;
    
    const code = getCode(ad);
    if (!containerRef.current || !code) return;

    if (isAdRendered(adKey)) {
      injectedRef.current = true;
      return;
    }

    const injectCode = () => {
      if (injectedRef.current) return;
      
      markAdRendered(adKey);
      injectedRef.current = true;

      const container = containerRef.current;
      if (!container) return;

      const temp = document.createElement('div');
      temp.innerHTML = code;

      Array.from(temp.childNodes).forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.tagName === 'SCRIPT') {
            const script = document.createElement('script');
            Array.from(node.attributes).forEach(attr => {
              script.setAttribute(attr.name, attr.value);
            });
            script.textContent = node.textContent;
            script.async = true;
            container.appendChild(script);
          } else {
            const clone = node.cloneNode(true);
            container.appendChild(clone);
            
            clone.querySelectorAll('script').forEach(oldScript => {
              const newScript = document.createElement('script');
              Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
              });
              newScript.textContent = oldScript.textContent;
              newScript.async = true;
              oldScript.parentNode.replaceChild(newScript, oldScript);
            });
          }
        } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          container.appendChild(document.createTextNode(node.textContent));
        }
      });

      if (onImpression) onImpression();
    };

    if (priority === 'high') {
      injectCode();
    } else {
      setTimeout(injectCode, 100);
    }
  }, [shouldInit, ad, adKey, onImpression, priority]);

  return <div ref={containerRef} className="ad-code-container" />;
};

// ============================================
// PRIORITY DETECTION
// ============================================
const ABOVE_FOLD_POSITIONS = ['header', 'top', 'sidebar_top', 'above_content', 'leaderboard'];

function getAdPriority(position, index) {
  if (index === 0) return 'high';
  if (ABOVE_FOLD_POSITIONS.some(p => position?.toLowerCase().includes(p))) return 'high';
  return 'low';
}

// ============================================
// MAIN AD DISPLAY COMPONENT
// ============================================
const AdDisplay = ({ position, className = '' }) => {
  const [adUnits, setAdUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const impressionTracked = useRef({});

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await fetch(`/api/ads/position/${position}`);
        const data = await response.json();
        
        if (data.success) {
          setAdUnits(data.data || []);
          console.log(`[AdDisplay] Ads updated for position: ${position}`, data.data?.length || 0);
        }
      } catch (error) {
        console.error('Error fetching ads:', error);
      } finally {
        setLoading(false);
      }
    };

    if (position && position !== 'head_code' && position !== 'interstitial') {
      fetchAds();
    } else {
      setLoading(false);
    }
  }, [position]);

  const trackImpression = useCallback(async (adId) => {
    if (impressionTracked.current[adId]) return;
    impressionTracked.current[adId] = true;

    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`/api/ads/${adId}/impression`);
      } else {
        fetch(`/api/ads/${adId}/impression`, { method: 'POST', keepalive: true });
      }
    } catch (e) {}
  }, []);

  const trackClick = useCallback(async (adId) => {
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`/api/ads/${adId}/click`);
      } else {
        fetch(`/api/ads/${adId}/click`, { method: 'POST', keepalive: true });
      }
    } catch (e) {}
  }, []);

  const renderAd = useCallback((ad, index) => {
    const adKey = getAdKey(ad, position);
    const uniqueId = `ad-${ad._id}-${position}-${index}`;
    const priority = getAdPriority(position, index);
    
    if (ad.gamConfig?.adUnitPath && isOutOfPageAd(ad.gamConfig.adUnitPath)) {
      return null;
    }

    // Google Ad Manager (GAM)
    if (ad.adType === 'gam') {
      return (
        <GAMAdUnit 
          key={adKey}
          ad={ad} 
          divId={uniqueId}
          position={position}
          priority={priority}
          onImpression={() => trackImpression(ad._id)}
        />
      );
    }

    // Google AdSense
    if (ad.adType === 'adsense') {
      return (
        <AdSenseUnit 
          key={adKey}
          ad={ad}
          adKey={adKey}
          priority={priority}
          onImpression={() => trackImpression(ad._id)}
        />
      );
    }

    // Image Banner
    if (ad.adType === 'image_banner' || ad.adType === 'image') {
      const imgConfig = ad.imageConfig || ad.imageBannerConfig || ad.imageBanner || {};
      return (
        <a 
          key={adKey}
          href={imgConfig.linkUrl || '#'}
          target={imgConfig.openInNewTab !== false ? '_blank' : '_self'}
          rel="noopener noreferrer"
          onClick={() => trackClick(ad._id)}
          style={{ display: 'block', textAlign: 'center' }}
        >
          <img 
            src={imgConfig.imageUrl} 
            alt={imgConfig.altText || 'Advertisement'}
            loading={priority === 'high' ? 'eager' : 'lazy'}
            decoding="async"
            style={{ maxWidth: '100%', height: 'auto' }}
            onLoad={() => trackImpression(ad._id)}
          />
        </a>
      );
    }

    // Video Ad
    if (ad.adType === 'video') {
      const videoConfig = ad.videoConfig || {};
      return (
        <video
          key={adKey}
          src={videoConfig.videoUrl}
          poster={videoConfig.posterUrl}
          autoPlay={videoConfig.autoPlay || videoConfig.autoplay}
          muted={videoConfig.muted !== false}
          loop={videoConfig.loop}
          controls={videoConfig.showControls}
          preload={priority === 'high' ? 'auto' : 'none'}
          style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }}
          onPlay={() => trackImpression(ad._id)}
        />
      );
    }

    // Custom Code
    const code = getCode(ad);
    if (['custom_code', 'custom_html', 'custom_js', 'native', 'amazon', 'affiliate'].includes(ad.adType) || code) {
      return (
        <CustomCodeAd 
          key={adKey}
          ad={ad}
          adKey={adKey}
          priority={priority}
          onImpression={() => trackImpression(ad._id)}
        />
      );
    }

    return null;
  }, [position, trackImpression, trackClick]);

  if (position === 'head_code' || position === 'interstitial') {
    return null;
  }

  if (loading || adUnits.length === 0) {
    return null;
  }

  return (
    <div className={`ad-display ${className}`}>
      {adUnits.map((ad, index) => renderAd(ad, index))}
    </div>
  );
};

// ============================================
// RENDER BETWEEN PARAGRAPH AD - FIXED
// ============================================
const RenderBetweenAd = ({ ad, adKey, position }) => {
  const containerRef = useRef(null);
  const injectedRef = useRef(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  const isVisibleRef = useRef(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const cleanup = observeElement(element, (isIntersecting) => {
      if (isIntersecting && !isVisibleRef.current) {
        isVisibleRef.current = true;
        setIsVisible(true);
      }
    });

    return cleanup;
  }, []);

  useEffect(() => {
    if (!isVisible || injectedRef.current) return;
    if (!containerRef.current) return;

    if (isAdRendered(adKey)) {
      injectedRef.current = true;
      return;
    }

    injectedRef.current = true;
    markAdRendered(adKey);

    const container = containerRef.current;

    // ========== GAM Ad ==========
    if (ad.adType === 'gam' && ad.gamConfig?.adUnitPath) {
      const divId = `gam-between-${ad._id}`;
      const gamDiv = document.createElement('div');
      gamDiv.id = divId;
      gamDiv.style.minWidth = '300px';
      gamDiv.style.minHeight = '300px';
      container.appendChild(gamDiv);

      loadGPTScript().then(() => {
        window.googletag.cmd.push(function() {
          let adSize;
          try {
            adSize = JSON.parse(ad.gamConfig.size);
          } catch {
            adSize = [[300, 250]];
          }

          const slot = window.googletag.defineSlot(ad.gamConfig.adUnitPath, adSize, divId);
          if (slot) {
            slot.addService(window.googletag.pubads());
            window.googletag.display(divId);
            queueSlotRefresh(slot);
            setIsLoaded(true);
          }
        });
      });
      return;
    }

    // ========== AdSense ==========
    if (ad.adType === 'adsense' && ad.adsenseConfig?.adClient) {
      const ins = document.createElement('ins');
      ins.className = 'adsbygoogle';
      ins.style.display = 'block';
      ins.setAttribute('data-ad-client', ad.adsenseConfig.adClient);
      ins.setAttribute('data-ad-slot', ad.adsenseConfig.adSlot);
      ins.setAttribute('data-ad-format', ad.adsenseConfig.adFormat || 'auto');
      ins.setAttribute('data-full-width-responsive', 'true');
      container.appendChild(ins);

      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        setIsLoaded(true);
      } catch (e) {}
      return;
    }

    // ========== Custom Code ==========
    const code = getCode(ad);
    if (code) {
      const temp = document.createElement('div');
      temp.innerHTML = code;
      
      const fragment = document.createDocumentFragment();

      Array.from(temp.childNodes).forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.tagName === 'SCRIPT') {
            const script = document.createElement('script');
            Array.from(node.attributes).forEach(attr => {
              script.setAttribute(attr.name, attr.value);
            });
            script.textContent = node.textContent;
            script.async = true;
            fragment.appendChild(script);
          } else {
            const clone = node.cloneNode(true);
            
            clone.querySelectorAll('script').forEach(oldScript => {
              const newScript = document.createElement('script');
              Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
              });
              newScript.textContent = oldScript.textContent;
              newScript.async = true;
              oldScript.parentNode.replaceChild(newScript, oldScript);
            });
            
            fragment.appendChild(clone);
          }
        } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          fragment.appendChild(document.createTextNode(node.textContent));
        }
      });

      container.appendChild(fragment);
      setIsLoaded(true);
    }
  }, [ad, adKey, isVisible]);

  return (
    <div 
      ref={containerRef} 
      className={`ad-code-container between-paragraph-ad ${isLoaded ? 'loaded' : ''}`}
      style={{ minHeight: isVisible ? 'auto' : '250px' }}
    />
  );
};

// ============================================
// CHAPTER CONTENT WITH ADS
// ============================================
export const ChapterContentWithAds = ({ content }) => {
  const [betweenAds, setBetweenAds] = useState([]);
  const [paragraphs, setParagraphs] = useState([]);

  // Clear ads khi navigate sang chapter mới
  useEffect(() => {
    const keysToDelete = [];
    adRegistry.rendered.forEach(key => {
      if (key.includes('between-')) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => adRegistry.rendered.delete(key));
    
    if (window.googletag?.apiReady) {
      window.googletag.cmd.push(function() {
        const slots = window.googletag.pubads().getSlots();
        const slotsToDestroy = slots.filter(slot => 
          slot.getSlotElementId()?.includes('gam-between')
        );
        if (slotsToDestroy.length > 0) {
          window.googletag.destroySlots(slotsToDestroy);
        }
      });
    }
  }, [content]);

  useEffect(() => {
    if (!content) {
      setParagraphs([]);
      return;
    }
    
    if (content.includes('<p>') || content.includes('<p ')) {
      const temp = document.createElement('div');
      temp.innerHTML = content;
      const pTags = temp.querySelectorAll('p');
      setParagraphs(Array.from(pTags).map(p => p.innerHTML));
    } else {
      setParagraphs(content.split(/\n+/).filter(p => p.trim()));
    }
  }, [content]);

  useEffect(() => {
    const fetchBetweenAds = async () => {
      try {
        const res = await fetch('/api/ads/position/chapter_between');
        const data = await res.json();
        if (data.success && data.data) {
          setBetweenAds(data.data.filter(ad => ad.isActive));
        }
      } catch (e) {
        console.error('Error fetching between ads:', e);
      }
    };
    fetchBetweenAds();
  }, []);

  const getAdsForParagraph = useCallback((paragraphIndex) => {
    const paragraphNumber = paragraphIndex + 1;
    
    return betweenAds.filter(ad => {
      const mode = ad.chapterSettings?.paragraphMode || 'fixed';
      const number = ad.chapterSettings?.paragraphNumber || ad.chapterSettings?.insertAfterParagraphs || 5;
      
      if (mode === 'auto') {
        return paragraphNumber % number === 0;
      } else {
        return paragraphNumber === number;
      }
    });
  }, [betweenAds]);
  
  if (paragraphs.length === 0) return null;
  
  return (
    <>
      {paragraphs.map((paragraph, index) => {
        const adsToShow = getAdsForParagraph(index);
        
        return (
          <React.Fragment key={index}>
            <p dangerouslySetInnerHTML={{ __html: paragraph }} />
            
            {index < paragraphs.length - 1 && adsToShow.map((ad, adIndex) => {
              const adKey = `between-${ad._id}-${index}-${adIndex}`;
              return <RenderBetweenAd key={adKey} ad={ad} adKey={adKey} position="chapter_between" />;
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

// ============================================
// UTILITY
// ============================================
export const clearAdRegistry = () => {
  adRegistry.rendered.clear();
  adRegistry.initialized.clear();
};

export default AdDisplay;